import React, { FC, FormEvent, useCallback, useRef, useState  } from 'react';

// * Components
import InputLegend from '../../InputLegend';
import PlatformIcon from '../../PlatformIcon';
import { InputField } from '../../InputField';
import { Error } from '../../Error';
import { PasswordField } from '../../PasswordField';

// * Hooks
import { useCreateChannelMutation } from '../../../features/api/apiSlice';

// * Local
import { ChannelFormProps } from './PlatformForm.interface';
import styles from "./PlatformForm.module.css";
import TextAreaField from '../../TextAreaField';

// * Helpers
const getDefaultErrors = () => ({ name: '', url: '', key: '' });

const ChannelForm: FC<ChannelFormProps> = ({
  id,
  selectedPlatform,
  closePopup
}) => {
  const [ createChannel ] = useCreateChannelMutation();

  // * Template refs
  const nameRef = useRef<HTMLTextAreaElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef<HTMLInputElement>(null);

  // * States
  const [ errorFields, setErrorFields ] = useState(getDefaultErrors());
  const [ errorText, setErrorText ] = useState('');

  // * Handlers
  const  handleInput = useCallback(() => {
    setErrorFields(getDefaultErrors());
    setErrorText('');
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const name = nameRef.current?.value;
    const url = urlRef.current?.value;
    const key = keyRef.current?.value;

    if (name === null || url === null || key === null) return;

    const isNameEmpty = name === '';
    const isUrlEmpty = url === '';
    const isKeyEmpty = key === '';

    if (isNameEmpty || isUrlEmpty || isKeyEmpty) {
      setErrorFields({
        name: isNameEmpty ? 'Пустое поле' : '',
        url: isUrlEmpty ? 'Пустое поле' : '',
        key: isKeyEmpty ? 'Пустое поле' : ''
      });

      return setErrorText('Заполните обязательные поля');
    }

    // * platform = 'other' используется для индикации, что в формочке выбрано "Другое"
    // * но в теле запроса не должно быть platform со значением "other"
    const platform = (selectedPlatform?.platform && selectedPlatform.platform !== 'other')
      ? selectedPlatform.platform
      : undefined;

    createChannel({
      name: name!,
      target_url: url!,
      key: key!,
      platform
    });

    closePopup();
  };

  return (
    <React.Fragment>
      {(selectedPlatform && selectedPlatform.platform !== 'other') && (
        <div className={styles.verbose}>
          <div className={styles.verbose__icon}>
            <PlatformIcon platform={selectedPlatform.platform} />
          </div>

          <p className={styles.verbose__text}>
            Прочтите инструкцию
            по подключению к сервису
            <a
              href={selectedPlatform.instructionLink}
              className={styles.verbose__link}
              target='_blank'
              rel='noreferrer'
            >
              здесь
            </a>
          </p>
        </div>
      )}

      <form id={id} onSubmit={handleSubmit} className={styles['form']} autoComplete="off">
        <InputLegend legend='Название канала'>
          <TextAreaField isError={Boolean(errorFields.name)} maxLength={100} placeholder="Название*" onInput={handleInput} ref={nameRef} />
        </InputLegend>

        <InputLegend legend='URL канала'>
          <InputField isError={Boolean(errorFields.url)} placeholder="URL*" onInput={handleInput} ref={urlRef} />
        </InputLegend>

        <InputLegend legend='Ключ канала'>
          <PasswordField isError={Boolean(errorFields.key)} icon="toggle" placeholder="Ключ*" onInput={handleInput} ref={keyRef} autoComplete="new-password" />
        </InputLegend>
      </form>

      <Error className={styles['error']}>{errorText}</Error>
    </React.Fragment>
  );
};

export default ChannelForm;